"use client";

import Link from "next/link";
import { useTranslation } from "@/lib/i18n/context";

export function CompareSection() {
  const { t } = useTranslation();
  const c = t.landing.cover.compare;

  return (
    <section className="relative max-w-[1320px] mx-auto px-5 md:px-12 py-20 md:py-[120px] z-[2]">
      <div className="max-w-[1080px] mx-auto text-left md:text-center">
        <div className="kicker-mono inline-flex items-center gap-2.5 mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--terracotta)]" /> {c.kicker}
        </div>
        <h2
          className="font-display text-[clamp(38px,4.2vw,58px)] leading-[1.04] tracking-[-0.02em] text-balance"
          style={{ fontWeight: 380 }}
        >
          {c.headlineA} <em className="text-[var(--terracotta)] italic">{c.headlineEm}</em>
        </h2>
        <p className="text-[18px] text-[var(--ink-soft)] max-w-[580px] mx-auto my-10 leading-[1.5]">
          {c.lead}
        </p>

        {/* Mobile-only "drag to compare" affordance — the table is wider than
           the viewport and scrolls horizontally with a sticky first column. */}
        <div className="md:hidden mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-[var(--ink-soft)]">
          <span className="jn-nudge-right">→</span> {c.dragHint}
        </div>

        <div className="relative">
          {/* Right-edge gradient cue that more columns exist (mobile). */}
          <div
            className="md:hidden pointer-events-none absolute top-0 right-0 bottom-0 w-10 z-[2]"
            style={{ background: "linear-gradient(to right, transparent, var(--paper))" }}
          />
          <div className="overflow-x-auto">
          <table className="w-full min-w-[600px] text-left border border-[var(--rule)] rounded-[4px] overflow-hidden bg-[var(--paper)]">
            <thead>
              <tr>
                <th className="sticky left-0 z-[1] bg-[var(--paper)] font-mono text-[12px] tracking-[0.16em] uppercase text-[var(--ink-soft)] font-medium p-[18px_22px] border-b border-[var(--rule)]">
                  {c.featureHead}
                </th>
                <th
                  className="font-mono text-[12px] tracking-[0.16em] uppercase font-semibold p-[18px_22px] border-b border-[var(--rule)] text-[var(--terracotta)]"
                  style={{
                    background: "rgba(184,83,26,0.07)",
                    borderLeft: "1px solid rgba(184,83,26,0.25)",
                    borderRight: "1px solid rgba(184,83,26,0.25)",
                  }}
                >
                  {c.journailHead}
                </th>
                {c.competitors.map((cc) => (
                  <th
                    key={cc}
                    className="font-mono text-[12px] tracking-[0.16em] uppercase text-[var(--ink-soft)] font-medium p-[18px_22px] border-b border-[var(--rule)]"
                  >
                    {cc}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {c.rows.map((row, ri) => {
                const isLast = ri === c.rows.length - 1;
                return (
                  <tr key={row.feature}>
                    <th
                      scope="row"
                      className={`sticky left-0 z-[1] bg-[var(--paper)] font-display italic text-[16px] text-[var(--ink)] font-normal p-[20px_22px] ${
                        isLast ? "" : "border-b border-[var(--rule)]"
                      }`}
                    >
                      {row.feature}
                    </th>
                    {row.values.map((v, vi) => {
                      const isJournail = vi === 0;
                      return (
                        <td
                          key={vi}
                          className={`p-[20px_22px] text-[15px] ${isLast ? "" : "border-b border-[var(--rule)]"}`}
                          style={
                            isJournail
                              ? {
                                  background: "rgba(184,83,26,0.07)",
                                  borderLeft: "1px solid rgba(184,83,26,0.25)",
                                  borderRight: "1px solid rgba(184,83,26,0.25)",
                                }
                              : undefined
                          }
                        >
                          {v ? (
                            <span className="inline-flex items-center justify-center w-[22px] h-[22px] text-[var(--terracotta)]">
                              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M4 10l4 4 8-9" />
                              </svg>
                            </span>
                          ) : (
                            <span className="text-[var(--ink-soft)]/50 text-[22px] inline-block leading-none">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
        <div className="font-mono text-[11px] tracking-[0.12em] text-[var(--ink-soft)] mt-5">{c.note}</div>
        <div className="mt-10 flex flex-col items-center gap-4">
          <div className="font-mono text-[11px] tracking-[0.16em] uppercase text-[var(--ink-soft)]">
            {c.seeMore}
          </div>
          <div className="flex flex-wrap justify-center gap-2.5">
            <Link
              href="/compare/journail-vs-motion"
              className="inline-flex items-center rounded-full border border-[var(--rule)] bg-[var(--paper-2)]/40 px-3.5 py-1.5 font-mono text-[12px] tracking-[0.04em] text-[var(--ink)] transition hover:border-[var(--terracotta)]/50 hover:text-[var(--terracotta)]"
            >
              Journail vs Motion
            </Link>
            <Link
              href="/compare/journail-vs-sunsama"
              className="inline-flex items-center rounded-full border border-[var(--rule)] bg-[var(--paper-2)]/40 px-3.5 py-1.5 font-mono text-[12px] tracking-[0.04em] text-[var(--ink)] transition hover:border-[var(--terracotta)]/50 hover:text-[var(--terracotta)]"
            >
              Journail vs Sunsama
            </Link>
            <Link
              href="/compare/journail-vs-akiflow"
              className="inline-flex items-center rounded-full border border-[var(--rule)] bg-[var(--paper-2)]/40 px-3.5 py-1.5 font-mono text-[12px] tracking-[0.04em] text-[var(--ink)] transition hover:border-[var(--terracotta)]/50 hover:text-[var(--terracotta)]"
            >
              Journail vs Akiflow
            </Link>
          </div>
          <Link
            href="/compare"
            className="font-mono text-[12px] tracking-[0.04em] text-[var(--terracotta)] hover:underline underline-offset-4"
          >
            {`${c.allComparisons} →`}
          </Link>
        </div>
      </div>
    </section>
  );
}
